import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Card,
    Container,
    Typography,
    Grid,
    Divider,
    Button,
    CircularProgress,
    Avatar,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CardContent,
    CardHeader
} from "@mui/material";
import { Link } from "react-router-dom";
import { DATE_FORMAT } from "config/constants";
import { getLocalDate, getTimeSlot, getImage, getImageFromURL } from "helpers/utils";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PaymentIcon from '@mui/icons-material/Payment';

const SellerOrderEditor = ({
  setState, selectedOrder, symbol, updateOrderClicked, onCancel, orderdetail, isLoading
}) => {
    const {
        order_type, order_id, _id, status: initialStatus, user, message: initialMessage
    } = selectedOrder || {};

    const {
        products, shipping, created_at: createdAt,
        total_order, total_price, total_shipping, shippingoption, total_tax,
        delivery_date, delivery_time, payment
    } = orderdetail || {};

    const [status, setStatus] = useState(initialStatus);
    const [message, setMessage] = useState(initialMessage || "");
    const [actualShippingCost, setActualShippingCost] = useState(total_shipping || 0);
    const [shippingCostError, setShippingCostError] = useState("");

    useEffect(() => {
        const orderFlow = order_type === 'pickup' 
            ? 'pickup' 
            : (payment?.method === 'codbased' ? 'delivery_cod' : 'delivery_upi');
            
        const nextStates = validTransitions[orderFlow]?.[initialStatus] || [];

        if (nextStates.length > 0) {
            setStatus(nextStates[0]);
        } else {
            setStatus(initialStatus);
        }
        
        setMessage(initialMessage || "");
    }, [initialStatus, initialMessage, order_type, payment]);
    
    // State machine for allowed transitions
    const validTransitions = {
      pickup: {
        new: ["accepted", "cancelled"],
        accepted: ["paymentpending", "cancelled"],
        paymentpending: ["paid", "cancelled"],
        paid: ["inprogress"],
        inprogress: ["readyforpickup"],
        readyforpickup: ["pickedup"],
        pickedup: []
      },
      delivery_upi: {
        new: ["accepted", "cancelled"],
        accepted: ["paymentpending", "cancelled"],
        paymentpending: ["paid", "cancelled"],
        paid: ["inprogress"],
        inprogress: ["readydelivery"],
        readydelivery: ["indelivery"],
        indelivery: ["delivered", "returned"],
        delivered: [],
        returned: []
      },
      delivery_cod: {
        new: ["accepted", "cancelled"],
        accepted: ["inprogress", "cancelled"],
        inprogress: ["readydelivery"],
        readydelivery: ["indelivery"],
        indelivery: ["deliveryaccepted", "returned"],
        deliveryaccepted: ["pendingpayment", "returned"],
        pendingpayment: ["paymentcompleted", "returned"],
        paymentcompleted: [],
        returned: []
      }
    };

    const handleUpdateOrder = () => {
        // Check if shipping cost is required for actualcost orders
        if (shippingoption === "actualcost" && initialStatus === "new" && status === "accepted") {
            if (!actualShippingCost || parseFloat(actualShippingCost) < 0) {
                setShippingCostError("Please enter a valid shipping cost");
                return;
            }
            const newTotalOrder = parseFloat(total_price || 0) + parseFloat(actualShippingCost) + parseFloat(total_tax || 0);
            updateOrderClicked({ 
                status, 
                order_id, 
                _id, 
                message,
                total_shipping: parseFloat(actualShippingCost),
                total_order: newTotalOrder
            });
        } else {
            updateOrderClicked({ status, order_id, _id, message });
        }
    };
    
    const handleAccept = () => {
        // Check if shipping cost is required for actualcost orders
        if (shippingoption === "actualcost" && initialStatus === "new") {
            if (!actualShippingCost || parseFloat(actualShippingCost) < 0) {
                setShippingCostError("Please enter a valid shipping cost before accepting");
                return;
            }
            const newTotalOrder = parseFloat(total_price || 0) + parseFloat(actualShippingCost) + parseFloat(total_tax || 0);
            updateOrderClicked({ 
                status: "accepted", 
                order_id, 
                _id, 
                message,
                total_shipping: parseFloat(actualShippingCost),
                total_order: newTotalOrder
            });
        } else {
            updateOrderClicked({ status: "accepted", order_id, _id, message });
        }
    };

    const handleReject = () => {
        updateOrderClicked({ status: "cancelled", order_id, _id, message });
    };

    const handleShippingCostChange = (event) => {
        const value = event.target.value;
        setActualShippingCost(value);
        setShippingCostError("");
    };

    const getStatusOptions = () => {
        const orderFlow = order_type === 'pickup' 
            ? 'pickup' 
            : (payment?.method === 'codbased' ? 'delivery_cod' : 'delivery_upi');
            
        const nextStates = validTransitions[orderFlow]?.[initialStatus] || [];

        const stateLabels = {
            accepted: "Accepted",
            cancelled: "Cancelled",
            paymentpending: "Payment Pending",
            paid: "Paid",
            inprogress: "In Progress",
            readyforpickup: "Ready for Pickup",
            pickedup: "Picked Up",
            readydelivery: "Ready for Delivery",
            indelivery: "Out for Delivery",
            delivered: "Delivered",
            returned: "Returned",
            deliveryaccepted: "Delivery Accepted",
            pendingpayment: "Pending Payment",
            paymentcompleted: "Payment Completed",
        };
        
        return nextStates.map(state => ({
            value: state,
            label: stateLabels[state] || state
        }));
    };

    const renderCustomerInfo = () => (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader 
              avatar={<PersonOutlineIcon sx={{ color: 'primary.main' }} />}
              title="Customer Information"
              titleTypographyProps={{ fontWeight: 600 }}
              sx={{ bgcolor: 'grey.200' }}
            />
            <CardContent>
                {user && (
                    <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.light' }}>{user.name ? user.name.charAt(0) : 'U'}</Avatar>
                        <Box>
                            <Typography variant="body1" fontWeight={600}>{user.name}</Typography>
                            {user.mobile && <Typography variant="body2" color="text.secondary">{user.mobile}</Typography>}
                        </Box>
                    </Box>
                )}
                <Divider sx={{ my: 1 }}/>
                {order_type === "delivery" && shipping ? (
                    <>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Delivery Address</Typography>
                        <Typography variant="body2">{shipping.name}</Typography>
                        <Typography variant="body2">{shipping.address}, {shipping.city}, {shipping.pincode}</Typography>
                        <Typography variant="body2">{shipping.phone}</Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Pickup Details</Typography>
                        {delivery_date && <Typography variant="body2">Date: {getLocalDate(delivery_date, DATE_FORMAT)}</Typography>}
                        {delivery_time && <Typography variant="body2">Time: {getTimeSlot(delivery_time)}</Typography>}
                        {!delivery_date && !delivery_time && <Typography variant="body2" color="text.secondary">No pickup time set.</Typography>}
                    </>
                )}
            </CardContent>
        </Card>
    );

    const renderStatusManager = () => {
        if (initialStatus === 'pending' || initialStatus === 'cancelled') {
             return (
                <Card variant="outlined">
                    <CardHeader 
                      avatar={<EditNoteIcon sx={{ color: 'primary.main' }} />}
                      title="Order Status"
                      titleTypographyProps={{ fontWeight: 600 }}
                      sx={{ bgcolor: 'grey.200' }}
                    />
                    <CardContent>
                         <Typography variant="h6" sx={{ textTransform: 'capitalize', mb: 2 }}>
                            {initialStatus}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                           {initialStatus === 'pending' ? 'Waiting for payment from customer.' : 'This order has been cancelled.'}
                        </Typography>
                    </CardContent>
                </Card>
             )
        }
        
        if (initialStatus === "new") {
            return (
                <Card variant="outlined">
                    <CardHeader 
                      avatar={<EditNoteIcon sx={{ color: 'primary.main' }} />}
                      title="New Order" 
                      titleTypographyProps={{ fontWeight: 600 }}
                      sx={{ bgcolor: 'grey.200' }}
                    />
                    <CardContent>
                        <Typography gutterBottom>
                            Review the order and accept or reject it.
                        </Typography>
                        
                        {shippingoption === "actualcost" && order_type === "delivery" && (
                            <Box sx={{ my: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    ⚠️ Actual Shipping Cost Required
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    This order requires actual shipping cost to be entered before acceptance.
                                </Typography>
                                <TextField
                                    fullWidth
                                    required
                                    type="number"
                                    label="Shipping Cost"
                                    value={actualShippingCost}
                                    onChange={handleShippingCostChange}
                                    error={!!shippingCostError}
                                    helperText={shippingCostError || `Enter the actual shipping/delivery cost for this order`}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1 }}>{symbol}</Typography>,
                                    }}
                                    sx={{ mt: 1, bgcolor: 'white' }}
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                                {actualShippingCost > 0 && (
                                    <Box sx={{ mt: 2, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            New Order Total: {symbol}{(parseFloat(total_price || 0) + parseFloat(actualShippingCost) + parseFloat(total_tax || 0)).toFixed(2)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            (Product: {symbol}{parseFloat(total_price || 0).toFixed(2)} + Shipping: {symbol}{parseFloat(actualShippingCost).toFixed(2)} + Tax: {symbol}{parseFloat(total_tax || 0).toFixed(2)})
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                        
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Reason for rejection (optional)"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            sx={{ my: 2 }}
                        />
                        <Box display="flex" justifyContent="space-between">
                            <Button variant="outlined" color="error" onClick={handleReject}>Reject</Button>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={handleAccept}
                                disabled={shippingoption === "actualcost" && order_type === "delivery" && (!actualShippingCost || parseFloat(actualShippingCost) < 0)}
                            >
                                Accept
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            );
        }

        const statusOptions = getStatusOptions();

        return (
            <Card variant="outlined">
                <CardHeader 
                  avatar={<EditNoteIcon sx={{ color: 'primary.main' }} />}
                  title="Update Order Status" 
                  titleTypographyProps={{ fontWeight: 600 }}
                  sx={{ bgcolor: 'grey.200' }}
                />
                <CardContent>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={status}
                            label="Status"
                            onChange={(e) => setStatus(e.target.value)}
                        >
                            {statusOptions.length > 0 ? (
                                statusOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>No further actions</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        onClick={handleUpdateOrder}
                        disabled={statusOptions.length === 0}
                    >
                        Update Order
                    </Button>
                </CardContent>
            </Card>
        );
    };
    
    const renderOrderItems = () => (
        <Card variant="outlined">
            <CardHeader 
              avatar={<ReceiptLongIcon sx={{ color: 'primary.main' }} />}
              title="Order Items"
              titleTypographyProps={{ fontWeight: 600 }}
              sx={{ bgcolor: 'grey.200' }}
            />
            <CardContent>
                {products && products.map((item, index) => (
                    <React.Fragment key={index}>
                        <Box display="flex" alignItems="center" my={1.5}>
                            <Avatar variant="rounded" src={getImage(item.product.images)} sx={{ width: 60, height: 60, mr: 2 }} />
                            <Box flexGrow={1}>
                                <Typography fontWeight={600}>{item.product.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {item.quantity} x {symbol}{parseFloat(item.seller_price).toFixed(2)}
                                </Typography>
                                {item.product_size && <Typography variant="caption" color="text.secondary">Size: {item.product_size}</Typography>}
                            </Box>
                            <Typography fontWeight={600}>
                                {symbol}{(item.quantity * parseFloat(item.seller_price)).toFixed(2)}
                            </Typography>
                        </Box>
                        {index < products.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </CardContent>
        </Card>
    );

    const renderOrderSummary = () => (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.200', borderRadius: 2 }}>
            <Grid container spacing={1} justifyContent="flex-end">
                <Grid item xs={6} sm={8}>
                    <Typography color="text.secondary">Subtotal:</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <Typography align="right">{symbol}{total_price ? parseFloat(total_price).toFixed(2) : '0.00'}</Typography>
                </Grid>
                <Grid item xs={6} sm={8}>
                    <Typography color="text.secondary">Delivery:</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <Typography align="right">
                        {shippingoption === "actualcost" ? "To be Added" : `${symbol}${total_shipping}` }
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={8}>
                    <Typography color="text.secondary">Tax:</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <Typography align="right">{symbol}{total_tax ? parseFloat(total_tax).toFixed(2) : '0.00'}</Typography>
                </Grid>
                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                <Grid item xs={6} sm={8}>
                    <Typography variant="h6" fontWeight={700}>Total:</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <Typography variant="h6" fontWeight={700} align="right">{symbol}{total_order ? parseFloat(total_order).toFixed(2) : '0.00'}</Typography>
                </Grid>
            </Grid>
        </Box>
    );

    const renderPaymentInfo = () => (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader 
              avatar={<PaymentIcon sx={{ color: 'primary.main' }} />}
              title="Payment Method"
              titleTypographyProps={{ fontWeight: 600 }}
              sx={{ bgcolor: 'grey.200' }}
            />
            <CardContent>
                <Typography variant="h6" fontWeight={600} color="primary.main">
                    {payment?.method === 'codbased' ? 'Cash on Delivery (COD)' : 'UPI / Online'}
                </Typography>
            </CardContent>

                          {payment?.screenshot &&  <img
                            width="100%"
                            src={getImageFromURL(payment.screenshot)}
                            />
                          }
        </Card>
    );

    if (isLoading || !orderdetail) {
        return (
            <Container sx={{ py: 3 }}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }
    
    return (
        <Container sx={{ py: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Order #{order_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Placed on: {getLocalDate(createdAt, `${DATE_FORMAT} hh:mm A`)}
                    </Typography>
                </Box>
                <Button variant="outlined" onClick={onCancel}>Back to Orders</Button>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    {renderOrderItems()}
                    {renderOrderSummary()}
                </Grid>
                <Grid item xs={12} md={4}>
                    {renderCustomerInfo()}
                    {renderStatusManager()}
                    {renderPaymentInfo()}
                </Grid>
            </Grid>
        </Container>
    );
};

SellerOrderEditor.propTypes = {
  setState: PropTypes.func.isRequired,
  selectedOrder: PropTypes.object.isRequired,
  symbol: PropTypes.string,
  updateOrderClicked: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  orderdetail: PropTypes.object,
  isLoading: PropTypes.bool
};

SellerOrderEditor.defaultProps = {
    symbol: '$',
    isLoading: false,
    orderdetail: null,
};

export default SellerOrderEditor;
